import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, MessageSquare, ClipboardList, Heart, MessageCircle } from "lucide-react";

const MEDAL = ["🥇", "🥈", "🥉"];

/**
 * Classement hebdomadaire des contributeurs les plus actifs.
 * Affichable n'importe où — par défaut compact (5 lignes).
 */
export default function WeeklyLeaderboard({ limit = 5, title = "Top contributeurs", subtitle = "7 derniers jours" }) {
  const [data, setData] = useState({ top: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/leaderboard/weekly?limit=${limit}`)
      .then((r) => setData(r.data || { top: [] }))
      .catch(() => setData({ top: [] }))
      .finally(() => setLoading(false));
  }, [limit]);

  return (
    <section className="phenix-card p-4 sm:p-5 bg-white" data-testid="weekly-leaderboard">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sun-400 to-pink-500 text-white grid place-items-center shadow-sm shrink-0">
            <Trophy size={16} strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="font-display font-bold text-base text-plum-900 leading-tight">{title}</h2>
            <p className="text-[11px] text-plum-700/65">{subtitle}</p>
          </div>
        </div>
        <span className="phenix-pill bg-pink-50 text-pink-600 text-[10px]">Live</span>
      </header>

      {loading ? (
        <p className="text-plum-700/50 italic text-[12px] py-4 text-center">Calcul du classement…</p>
      ) : data.top.length === 0 ? (
        <p className="text-plum-700/50 italic text-[12px] py-4 text-center">
          Pas encore d&apos;activité cette semaine. Soyez le premier !
        </p>
      ) : (
        <ol className="space-y-2">
          {data.top.map((u) => {
            const medal = u.rank <= 3 ? MEDAL[u.rank - 1] : null;
            const isPodium = u.rank <= 3;
            return (
              <li
                key={u.user_id}
                data-testid={`leaderboard-rank-${u.rank}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                  isPodium
                    ? "bg-gradient-to-r from-sun-50/70 to-cream-50 border-sun-200"
                    : "bg-cream-50/40 border-cream-200"
                }`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center text-[14px] font-bold ${
                  u.rank === 1 ? "bg-sun-400 text-plum-900" :
                  u.rank === 2 ? "bg-cream-200 text-plum-800" :
                  u.rank === 3 ? "bg-pink-200 text-plum-800" :
                  "bg-cream-100 text-plum-700/65"
                }`}>
                  {medal || `#${u.rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[14px] text-plum-900 leading-tight truncate">{u.pseudo}</div>
                  <div className="text-[10px] text-plum-700/55 mt-0.5 flex items-center gap-2 flex-wrap">
                    {u.actions.feedback > 0 && (
                      <span className="inline-flex items-center gap-0.5"><MessageSquare size={9} /> {u.actions.feedback}</span>
                    )}
                    {u.actions.survey > 0 && (
                      <span className="inline-flex items-center gap-0.5"><ClipboardList size={9} /> {u.actions.survey}</span>
                    )}
                    {u.actions.comment > 0 && (
                      <span className="inline-flex items-center gap-0.5"><MessageCircle size={9} /> {u.actions.comment}</span>
                    )}
                    {u.actions.reaction > 0 && (
                      <span className="inline-flex items-center gap-0.5"><Heart size={9} /> {u.actions.reaction}</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-extrabold text-lg text-plum-900 leading-none tabular-nums">{u.score}</div>
                  <div className="text-[9px] text-plum-700/55 uppercase tracking-wider">pts</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <p className="mt-3 text-[10px] text-plum-700/50 italic leading-relaxed">
        Remontée : 5 pts · Sondage : 3 pts · Commentaire : 2 pts · Réaction : 1 pt
      </p>
    </section>
  );
}
